﻿/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  Alert,
  Dimensions,
  Animated,
  TextInput,
  FlatList,
  Modal,
  RefreshControl,
  ActivityIndicator,
  Platform,
  AppState,
  KeyboardAvoidingView,
  Keyboard,
  TouchableWithoutFeedback,
  Linking,
  Share,
  Image,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  MaterialIcons,
  Feather,
  AntDesign,
  Entypo,
  FontAwesome,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import * as Calendar from 'expo-calendar';
import * as Notifications from 'expo-notifications';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { Video } from 'expo-video';
import { Audio } from 'expo-audio';
const { width, height } = Dimensions.get('window');

// Configure notifications
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowBanner: true,
    shouldShowList: true,
    shouldPlaySound: true,
    shouldSetBadge: true,
  }),
});

// ─── JobEng Learning API ─────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const getLearnHeaders = async () => {
  let tok = null;
  try {
    tok = (await AsyncStorage.getItem('auth_token')) ||
          (await AsyncStorage.getItem('userToken')) ||
          (await AsyncStorage.getItem('token'));
  } catch { /* ignore */ }
  return {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
  };
};

// Fetch rows from a /api/data table; returns [] on any failure so callers fall back to mocks.
const fetchLearnTable = async (table, query = '') => {
  try {
    const h = await getLearnHeaders();
    const res = await fetch(`${API_BASE}/api/data/${table}?limit=50${query ? '&' + query : ''}`, { headers: h });
    if (!res.ok) return [];
    const json = await res.json().catch(() => null);
    const rows = Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : [];
    return rows;
  } catch { return []; }
};

const sNum = (v, d = 0) => { const n = Number(v); return Number.isFinite(n) ? n : d; };
const sStr = (v, d = '') => (v === null || v === undefined ? d : String(v));

// Map a DB course row to the rich UI shape, filling visual defaults the table lacks.
const mapCourseRow = (r, fallbackColor = '#0e4e96', fallbackIcon = 'book-open-variant') => {
  let modules = [];
  try { modules = Array.isArray(r.modules) ? r.modules : (typeof r.modules === 'string' ? JSON.parse(r.modules) : []); } catch { modules = []; }
  const price = sNum(r.price);
  return {
    id: sStr(r.id || r.course_id || Math.random().toString(36).slice(2)),
    title: sStr(r.title || r.name || r.course_name, 'Untitled Course'),
    subtitle: sStr(r.subtitle || r.short_description || r.category, ''),
    instructor: sStr(r.instructor || r.instructor_name || r.author, 'JobEng Academy'),
    duration: sStr(r.duration || (r.duration_hours ? `${r.duration_hours} hours` : ''), 'Self-paced'),
    rating: sNum(r.rating || r.avg_rating, 4.7),
    students: sNum(r.students || r.enrollment_count || r.enrollments, 0),
    level: sStr(r.level || r.difficulty, 'All Levels'),
    isFree: price === 0 || r.is_free === true || r.isFree === true,
    price,
    color: sStr(r.color, fallbackColor),
    icon: sStr(r.icon, fallbackIcon),
    progress: sNum(r.progress, 0),
    category: sStr(r.category || r.category_id, 'general'),
    description: sStr(r.description, ''),
    modules: modules.length ? modules : [],
    videoPreview: sStr(r.video_preview || r.preview_url || r.videoPreview, ''),
    thumbnail: sStr(r.thumbnail || r.thumbnail_url || r.image_url || r.cover_image, ''),
    platform: sStr(r.platform, 'JobEng Academy'),
    certificateAvailable: r.certificate_available === true || r.has_certificate === true,
    enrollmentLink: sStr(r.enrollment_link || r.enroll_url, ''),
    lastAccessed: (r.last_accessed ? new Date(r.last_accessed) : new Date()).toISOString(),
    _raw: r,
  };
};

// Safely format a timestamp that may be a Date, ISO string, number, or undefined
// (timestamps become strings after JSON / AsyncStorage round-trips).
const formatMsgTime = (ts) => {
  if (!ts) return '';
  const d = ts instanceof Date ? ts : new Date(ts);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const LearningDashboard = () => {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const [userData, setUserData] = useState(null);
  const [fadeAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(50));
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [bookmarkedCourses, setBookmarkedCourses] = useState([]);
  const [ratedCourses, setRatedCourses] = useState({});
  const [appState, setAppState] = useState(AppState.currentState);
  const [darkMode, setDarkMode] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTab, setSelectedTab] = useState('courses');
  
  // AI Chat States
  const [showAIChat, setShowAIChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  // Live courses data state
  const [liveCourses, setLiveCourses] = useState([]);
  const [currentCourses, setCurrentCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [freeCourses, setFreeCourses] = useState([]);
  
  // UK Universities Data
  const [ukUniversities, setUkUniversities] = useState([]);
  
  // Engineering Books Data
  const [engineeringBooks, setEngineeringBooks] = useState([]);
  
  // Video player states
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  // Side Menu States
  const [menuVisible, setMenuVisible] = useState(false);
  const [dbConnected, setDbConnected] = useState(null); // null=unknown, true=live DB, false=cache/mock
  const [menuAnim] = useState(new Animated.Value(-300));

  // Ref for debouncing
  const searchTimeoutRef = useRef(null);
  const appStateSubRef = useRef(null);
  const netInfoUnsubRef = useRef(null);

  // Test IDs for automation testing
  const testIDs = {
    dashboard: 'learning-dashboard',
    welcomeSection: 'welcome-section',
    aiButton: 'ai-assistant-button',
    statsSection: 'learning-stats',
    liveCourses: 'live-courses-section',
    freeCourses: 'free-courses-section',
    ukUniversities: 'uk-universities-section',
    engineeringBooks: 'engineering-books-section',
    courseCard: 'course-card-',
    logoutButton: 'logout-button',
    refreshControl: 'refresh-control',
    searchInput: 'search-courses-input',
    offlineBanner: 'offline-banner',
    darkModeToggle: 'dark-mode-toggle',
    categoryFilter: 'category-filter-',
  };

  useEffect(() => {
    loadInitialData();
    setupNetInfoListener();
    setupAppStateListener();
    requestNotificationPermissions();
    loadChatHistory();
    startAnimations();
    
    return () => {
      // Modern RN: AppState/NetInfo listeners return a subscription with .remove()
      if (netInfoUnsubRef.current) {
        try { netInfoUnsubRef.current(); } catch {}
        netInfoUnsubRef.current = null;
      }
      if (appStateSubRef.current) {
        try { appStateSubRef.current.remove(); } catch {}
        appStateSubRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    filterCourses();
  }, [searchQuery, selectedCategory, currentCourses]);

  const setupNetInfoListener = () => {
    const unsubscribe = NetInfo.addEventListener(state => {
      setIsOnline(state.isConnected);
      if (state.isConnected) {
        syncOfflineData();
      }
    });
    netInfoUnsubRef.current = unsubscribe;
    return unsubscribe;
  };

  const setupAppStateListener = () => {
    appStateSubRef.current = AppState.addEventListener('change', handleAppStateChange);
  };

  const handleAppStateChange = (nextAppState) => {
    if (appState.match(/inactive|background/) && nextAppState === 'active') {
      refreshData();
    }
    setAppState(nextAppState);
  };

  const requestNotificationPermissions = async () => {
    try {
      const { status } = await Notifications.requestPermissionsAsync();
      if (status !== 'granted') {
        console.log('Notification permission not granted');
      }
    } catch (error) {
      console.error('Error requesting notification permissions:', error);
    }
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([
        loadUserData(),
        loadLiveCourses(),
        loadCurrentCourses(),
        loadFreeCourses(),
        loadUKUniversities(),
        loadEngineeringBooks(),
        loadBookmarks(),
        loadRatings(),
        loadDarkModePreference(),
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    if (!isOnline) return;
    
    setIsRefreshing(true);
    try {
      await Promise.all([
        loadUserData(),
        loadLiveCourses(),
        loadCurrentCourses(),
        loadFreeCourses(),
        loadUKUniversities(),
        loadEngineeringBooks(),
      ]);
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const loadUKUniversities = async () => {
    try {
      const mockUKUniversities = [
        {
          id: 'U001',
          name: 'University of Cambridge',
          location: 'Cambridge, England',
          ranking: '#1 in UK (Engineering)',
          specializations: [
            'Civil Engineering',
            'Mechanical Engineering',
            'Electrical Engineering',
            'Chemical Engineering'
          ],
          website: 'https://www.cam.ac.uk/engineering',
          color: '#002147',
          icon: 'school',
          description: 'World-renowned engineering programs with strong industry links.',
          tuition: '£9,250/year (UK)',
          intlTuition: '£35,000-£45,000/year',
          applicationDeadline: '15 October',
          entryRequirements: 'A*AA including Mathematics and Physics',
          notableAlumni: 'Sir Isaac Newton, Charles Babbage, James Clerk Maxwell'
        },
        {
          id: 'U002',
          name: 'University of Oxford',
          location: 'Oxford, England',
          ranking: '#2 in UK (Engineering)',
          specializations: [
            'Engineering Science',
            'Biomedical Engineering',
            'Materials Science',
            'Aerospace Engineering'
          ],
          website: 'https://www.ox.ac.uk/engineering',
          color: '#002147',
          icon: 'school',
          description: 'Interdisciplinary engineering programs with research excellence.',
          tuition: '£9,250/year (UK)',
          intlTuition: '£38,000-£48,000/year',
          applicationDeadline: '15 October',
          entryRequirements: 'A*A*A including Mathematics and Physics',
          notableAlumni: 'Tim Berners-Lee, Stephen Hawking, Robert Hooke'
        },
        {
          id: 'U003',
          name: 'Imperial College London',
          location: 'London, England',
          ranking: '#3 in UK (Engineering)',
          specializations: [
            'Aeronautical Engineering',
            'Computing',
            'Electrical & Electronic',
            'Chemical Engineering'
          ],
          website: 'https://www.imperial.ac.uk/engineering',
          color: '#00549F',
          icon: 'school',
          description: 'Specialized engineering college with strong industry connections.',
          tuition: '£9,250/year (UK)',
          intlTuition: '£36,000-£42,000/year',
          applicationDeadline: '15 January',
          entryRequirements: 'A*A*A including Mathematics and Physics',
          notableAlumni: 'Alexander Fleming, H. G. Wells, Brian May'
        },
        {
          id: 'U004',
          name: 'University of Manchester',
          location: 'Manchester, England',
          ranking: '#4 in UK (Engineering)',
          specializations: [
            'Chemical Engineering',
            'Mechanical Engineering',
            'Civil Engineering',
            'Computer Science'
          ],
          website: 'https://www.manchester.ac.uk/engineering',
          color: '#E31B23',
          icon: 'school',
          description: 'Largest engineering faculty in the UK with extensive facilities.',
          tuition: '£9,250/year (UK)',
          intlTuition: '£28,000-£35,000/year',
          applicationDeadline: '25 January',
          entryRequirements: 'AAA including Mathematics and Science',
          notableAlumni: 'Alan Turing, Ernest Rutherford, John Dalton'
        },
        {
          id: 'U005',
          name: 'University of Edinburgh',
          location: 'Edinburgh, Scotland',
          ranking: '#5 in UK (Engineering)',
          specializations: [
            'Electronics & Computer Science',
            'Civil & Environmental',
            'Mechanical Engineering',
            'Chemical Engineering'
          ],
          website: 'https://www.ed.ac.uk/engineering',
          color: '#003366',
          icon: 'school',
          description: 'Leading Scottish university with strong research output.',
          tuition: '£1,820/year (Scotland/EU)',
          intlTuition: '£28,950-£34,800/year',
          applicationDeadline: '30 June',
          entryRequirements: 'AAA-ABB including Mathematics',
          notableAlumni: 'James Clerk Maxwell, Alexander Graham Bell, Charles Darwin'
        },
        {
          id: 'U006',
          name: 'University of Bristol',
          location: 'Bristol, England',
          ranking: '#6 in UK (Engineering)',
          specializations: [
            'Aerospace Engineering',
            'Civil Engineering',
            'Mechanical Engineering',
            'Electrical Engineering'
          ],
          website: 'https://www.bristol.ac.uk/engineering',
          color: '#E03C31',
          icon: 'school',
          description: 'Innovative engineering programs with strong industry placement.',
          tuition: '£9,250/year (UK)',
          intlTuition: '£26,000-£34,000/year',
          applicationDeadline: '29 January',
          entryRequirements: 'A*AA including Mathematics',
          notableAlumni: 'Paul Dirac, David Olusoga, Will Hutton'
        },
        {
          id: 'U007',
          name: 'University of Sheffield',
          location: 'Sheffield, England',
          ranking: '#7 in UK (Engineering)',
          specializations: [
            'Materials Science & Engineering',
            'Civil & Structural',
            'Mechanical Engineering',
            'Electronic Engineering'
          ],
          website: 'https://www.sheffield.ac.uk/engineering',
          color: '#0C2340',
          icon: 'school',
          description: 'Renowned for materials science and engineering research.',
          tuition: '£9,250/year (UK)',
          intlTuition: '£25,000-£33,000/year',
          applicationDeadline: '29 January',
          entryRequirements: 'AAA including Mathematics and Science',
          notableAlumni: 'Harold Kroto, Helen Sharman, David Blunkett'
        },
        {
          id: 'U008',
          name: 'University of Leeds',
          location: 'Leeds, England',
          ranking: '#8 in UK (Engineering)',
          specializations: [
            'Civil Engineering',
            'Mechanical Engineering',
            'Electronic Engineering',
            'Chemical Engineering'
          ],
          website: 'https://eps.leeds.ac.uk/engineering',
          color: '#014421',
          icon: 'school',
          description: 'Comprehensive engineering programs with excellent facilities.',
          tuition: '£9,250/year (UK)',
          intlTuition: '£25,000-£32,000/year',
          applicationDeadline: '29 January',
          entryRequirements: 'AAA including Mathematics',
          notableAlumni: 'Jack Straw, Mark Knopfler, Chris Pine'
        }
      ];
      const rows = await fetchLearnTable('uk_universities');
      const mapped = rows.map((r) => {
        let specializations = [];
        try { specializations = Array.isArray(r.specializations) ? r.specializations : (typeof r.specializations === 'string' ? JSON.parse(r.specializations) : []); }
        catch { specializations = sStr(r.specializations) ? sStr(r.specializations).split(',').map((s) => s.trim()) : []; }
        return {
          id: sStr(r.id || Math.random().toString(36).slice(2)),
          name: sStr(r.name || r.university_name, 'University'),
          location: sStr(r.location || r.city, 'United Kingdom'),
          ranking: sNum(r.ranking || r.rank, 0),
          specializations,
          website: sStr(r.website || r.url, ''),
          color: sStr(r.color, '#0e4e96'),
          icon: sStr(r.icon, 'school'),
          description: sStr(r.description, ''),
          tuition: sStr(r.tuition || r.tuition_fee, ''),
          intlTuition: sStr(r.intl_tuition || r.international_tuition, ''),
          applicationDeadline: sStr(r.application_deadline || r.deadline, ''),
          entryRequirements: sStr(r.entry_requirements, ''),
          notableAlumni: sStr(r.notable_alumni, ''),
          _raw: r,
        };
      });
      if (mapped.length) {
        setUkUniversities(mapped);
        try { await AsyncStorage.setItem('cache_ukUniversities', JSON.stringify(mapped)); } catch {}
      } else {
        const cached = await AsyncStorage.getItem('cache_ukUniversities');
        setUkUniversities(cached ? JSON.parse(cached) : mockUKUniversities);
      }
    } catch (error) {
      console.error('Error loading UK universities:', error);
      throw error;
    }
  };

  const loadEngineeringBooks = async () => {
    try {
      const mockEngineeringBooks = [
        {
          id: 'B001',
          title: 'Mechanical Engineering Design',
          author: 'Joseph E. Shigley, Charles R. Mischke, Richard G. Budynas',
          edition: '11th Edition',
          publisher: 'McGraw-Hill',
          year: '2020',
          isbn: '978-0073398211',
          category: 'mechanical',
          pages: 1120,
          level: 'Undergraduate/Graduate',
          color: '#FF6B35',
          icon: 'cog',
          coverImage: 'https://images-na.ssl-images-amazon.com/images/I/81h7B+1hL4L.jpg',
          description: 'Comprehensive textbook covering machine design fundamentals.',
          topics: [
            'Stress Analysis',
            'Fatigue Failure',
            'Gears & Bearings',
            'Shaft Design',
            'Springs'
          ],
          amazonLink: 'https://www.amazon.com/Mechanical-Engineering-Design-Joseph-Shigley/dp/0073398214',
          pdfAvailable: true,
          rating: 4.7,
          price: '£75.99'
        },
        {
          id: 'B002',
          title: 'Fundamentals of Electric Circuits',
          author: 'Charles K. Alexander, Matthew N. O. Sadiku',
          edition: '7th Edition',
          publisher: 'McGraw-Hill',
          year: '2021',
          isbn: '978-1260226409',
          category: 'electrical',
          pages: 992,
          level: 'Undergraduate',
          color: '#FFD700',
          icon: 'flash',
          coverImage: 'https://images-na.ssl-images-amazon.com/images/I/81F4N3l1AQL.jpg',
          description: 'Comprehensive coverage of circuit analysis and design.',
          topics: [
            'Basic Laws',
            'Circuit Theorems',
            'AC Circuit Analysis',
            'Three-Phase Circuits'
          ],
          amazonLink: 'https://www.amazon.com/Fundamentals-Electric-Circuits-Charles-Alexander/dp/1260226409',
          pdfAvailable: true,
          rating: 4.6,
          price: '£89.99'
        },
      ];
      
      const rows = await fetchLearnTable('engineering_books');
      const mapped = rows.map((r) => {
        let topics = [];
        try { topics = Array.isArray(r.topics) ? r.topics : (typeof r.topics === 'string' ? JSON.parse(r.topics) : []); }
        catch { topics = sStr(r.topics) ? sStr(r.topics).split(',').map((s) => s.trim()) : []; }
        return {
          id: sStr(r.id || Math.random().toString(36).slice(2)),
          title: sStr(r.title || r.name, 'Untitled Book'),
          author: sStr(r.author, 'Unknown'),
          edition: sStr(r.edition, ''),
          publisher: sStr(r.publisher, ''),
          year: sStr(r.year || r.publication_year, ''),
          isbn: sStr(r.isbn, ''),
          category: sStr(r.category, 'general'),
          pages: sNum(r.pages, 0),
          level: sStr(r.level, 'All Levels'),
          color: sStr(r.color, '#5E35B1'),
          icon: sStr(r.icon, 'book'),
          coverImage: sStr(r.cover_image || r.coverImage || r.thumbnail, ''),
          description: sStr(r.description, ''),
          topics,
          amazonLink: sStr(r.amazon_link || r.amazonLink || r.buy_url, ''),
          pdfAvailable: r.pdf_available === true || r.pdfAvailable === true || !!r.pdf_url,
          pdfUrl: sStr(r.pdf_url || r.file_url, ''),
          rating: sNum(r.rating, 4.5),
          price: sNum(r.price, 0),
          _raw: r,
        };
      });
      if (mapped.length) {
        setEngineeringBooks(mapped);
        try { await AsyncStorage.setItem('cache_engineeringBooks', JSON.stringify(mapped)); } catch {}
      } else {
        const cached = await AsyncStorage.getItem('cache_engineeringBooks');
        setEngineeringBooks(cached ? JSON.parse(cached) : mockEngineeringBooks);
      }
    } catch (error) {
      console.error('Error loading engineering books:', error);
      throw error;
    }
  };

  // Side Menu Functions
  const toggleMenu = () => {
    if (menuVisible) {
      Animated.timing(menuAnim, {
        toValue: -300,
        duration: 300,
        useNativeDriver: true,
      }).start(() => setMenuVisible(false));
    } else {
      setMenuVisible(true);
      Animated.timing(menuAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  };

  const navigateToScreen = (screenName) => {
    toggleMenu();
    setTimeout(() => {
      navigation.navigate(screenName);
    }, 300);
  };

  const renderSideMenu = () => (
    <>
      {menuVisible && (
        <TouchableWithoutFeedback onPress={toggleMenu}>
          <Animated.View 
            style={[
              styles.menuOverlay,
              {
                opacity: menuAnim.interpolate({
                  inputRange: [-300, 0],
                  outputRange: [0, 0.5]
                })
              }
            ]}
          />
        </TouchableWithoutFeedback>
      )}

      <Animated.View 
        style={[
          styles.sideMenu,
          {
            transform: [{ translateX: menuAnim }]
          }
        ]}
      >
        <LinearGradient
          colors={darkMode ? ['#2d3748', '#4a5568'] : ['#667eea', '#764ba2']}
          style={styles.menuHeader}
        >
          <TouchableOpacity onPress={toggleMenu} style={styles.menuCloseButton}>
            <MaterialCommunityIcons name="close" size={24} color="#ffffff" />
          </TouchableOpacity>
          <View style={styles.menuUserInfo}>
            <LinearGradient
              colors={darkMode ? ['#805ad5', '#d53f8c'] : ['#f093fb', '#f5576c']}
              style={styles.menuAvatar}
            >
              <Text style={styles.menuAvatarText}>
                {getUserName().charAt(0).toUpperCase()}
              </Text>
            </LinearGradient>
            <Text style={styles.menuUserName}>{getUserName()}</Text>
            <Text style={styles.menuUserEmail}>
              {userData?.email || 'student@engineering.com'}
            </Text>
          </View>
        </LinearGradient>

        <ScrollView style={styles.menuItems} showsVerticalScrollIndicator={false}>
          <View style={styles.menuSection}>
            <Text style={styles.menuSectionTitle}>LEARNING HUB</Text>
            <TouchableOpacity 
              style={styles.menuItem}
              onPress={() => navigateToScreen('Learning')}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons name="home" size={24} color="#667eea" />
              </View>
              <Text style={styles.menuItemText}>Learning Home</Text>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#9ca3af" />
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.menuItem}
              onPress={() => navigateToScreen('MyCourses')}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons name="book-open-variant" size={24} color="#3b82f6" />
              </View>
              <Text style={styles.menuItemText}>My Courses</Text>
              <View style={styles.badge}>
                <Text style={styles.badgeText}>4</Text>
              </View>
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.menuItem}
              onPress={() => navigateToScreen('LiveSessionsScreen')}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons name="video" size={24} color="#ef4444" />
              </View>
              <Text style={styles.menuItemText}>Live Sessions</Text>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#9ca3af" />
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.menuItem}
              onPress={() => navigateToScreen('EngineeringBooksScreen')}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons name="book-open-page-variant" size={24} color="#10b981" />
              </View>
              <Text style={styles.menuItemText}>Engineering Books</Text>
              <View style={styles.badge}>
                <Text style={styles.badgeText}>{engineeringBooks.length}</Text>
              </View>
            </TouchableOpacity>
          </View>

          <View style={styles.menuSection}>
            <Text style={styles.menuSectionTitle}>UK UNIVERSITIES</Text>
            <TouchableOpacity 
              style={styles.menuItem}
              onPress={() => navigateToScreen('UKUniversities')}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons name="school" size={24} color="#8b5cf6" />
              </View>
              <Text style={styles.menuItemText}>University Rankings</Text>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#9ca3af" />
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.menuItem}
              onPress={() => navigateToScreen('UniversityApplications')}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons name="file-document-edit" size={24} color="#f59e0b" />
              </View>
              <Text style={styles.menuItemText}>Applications Guide</Text>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#9ca3af" />
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.menuItem}
              onPress={() => navigateToScreen('Scholarships')}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons name="cash-multiple" size={24} color="#06b6d4" />
              </View>
              <Text style={styles.menuItemText}>Scholarships</Text>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#9ca3af" />
            </TouchableOpacity>
          </View>

          <View style={styles.menuSection}>
            <Text style={styles.menuSectionTitle}>RESOURCES</Text>
            <TouchableOpacity 
              style={styles.menuItem}
              onPress={() => navigateToScreen('FreeCourses')}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons name="currency-usd-off" size={24} color="#22c55e" />
              </View>
              <Text style={styles.menuItemText}>Free Courses</Text>
              <View style={styles.badge}>
                <Text style={styles.badgeText}>{freeCourses.length}</Text>
              </View>
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.menuItem}
              onPress={() => navigateToScreen('PracticeExams')}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons name="clipboard-text" size={24} color="#ec4899" />
              </View>
              <Text style={styles.menuItemText}>Practice Exams</Text>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#9ca3af" />
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.menuItem}
              onPress={() => navigateToScreen('VideoLibrary')}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons name="video" size={24} color="#6366f1" />
              </View>
              <Text style={styles.menuItemText}>Video Library</Text>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#9ca3af" />
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.menuItem}
              onPress={() => navigateToScreen('ResearchPapers')}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons name="file-document" size={24} color="#14b8a6" />
              </View>
              <Text style={styles.menuItemText}>Research Papers</Text>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#9ca3af" />
            </TouchableOpacity>
          </View>

          <View style={styles.menuSection}>
            <Text style={styles.menuSectionTitle}>CAREER & JOBS</Text>
            <TouchableOpacity 
              style={styles.menuItem}
              onPress={() => navigateToScreen('Internships')}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons name="briefcase" size={24} color="#f97316" />
              </View>
              <Text style={styles.menuItemText}>Internships</Text>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#9ca3af" />
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.menuItem}
              onPress={() => navigateToScreen('JobBoard')}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons name="account-tie" size={24} color="#3b82f6" />
              </View>
              <Text style={styles.menuItemText}>Job Board</Text>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#9ca3af" />
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.menuItem}
              onPress={() => navigateToScreen('CareerGuidance')}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons name="chart-line" size={24} color="#8b5cf6" />
              </View>
              <Text style={styles.menuItemText}>Career Guidance</Text>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#9ca3af" />
            </TouchableOpacity>
          </View>

          <View style={styles.menuSection}>
            <Text style={styles.menuSectionTitle}>COMMUNITY</Text>
            <TouchableOpacity 
              style={styles.menuItem}
              onPress={() => navigateToScreen('StudyGroups')}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons name="account-group" size={24} color="#06b6d4" />
              </View>
              <Text style={styles.menuItemText}>Study Groups</Text>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#9ca3af" />
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.menuItem}
              onPress={() => navigateToScreen('Forums')}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons name="forum" size={24} color="#10b981" />
              </View>
              <Text style={styles.menuItemText}>Discussion Forums</Text>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#9ca3af" />
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.menuItem}
              onPress={() => navigateToScreen('AskExpert')}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons name="school" size={24} color="#f59e0b" />
              </View>
              <Text style={styles.menuItemText}>Ask an Expert</Text>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#9ca3af" />
            </TouchableOpacity>
          </View>

          <View style={styles.menuSection}>
            <Text style={styles.menuSectionTitle}>SETTINGS</Text>
            <TouchableOpacity 
              style={styles.menuItem}
              onPress={() => navigateToScreen('ProfileSettings')}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons name="account-cog" size={24} color="#6b7280" />
              </View>
              <Text style={styles.menuItemText}>Profile Settings</Text>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#9ca3af" />
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.menuItem}
              onPress={toggleDarkMode}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons 
                  name={darkMode ? "weather-sunny" : "weather-night"} 
                  size={24} 
                  color="#f59e0b" 
                />
              </View>
              <Text style={styles.menuItemText}>
                {darkMode ? 'Light Mode' : 'Dark Mode'}
              </Text>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#9ca3af" />
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.menuItem}
              onPress={() => navigateToScreen('LearningNotifications')}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons name="bell" size={24} color="#ef4444" />
              </View>
              <Text style={styles.menuItemText}>Notifications</Text>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#9ca3af" />
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.menuItem}
              onPress={() => navigateToScreen('LearningHelpSupport')}
            >
              <View style={styles.menuIconContainer}>
                <MaterialCommunityIcons name="help-circle" size={24} color="#3b82f6" />
              </View>
              <Text style={styles.menuItemText}>Help & Support</Text>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#9ca3af" />
            </TouchableOpacity>
          </View>
        </ScrollView>

        <View style={styles.menuFooter}>
          <TouchableOpacity
            style={styles.logoutButtonMenu}
            onPress={() => navigateToScreen('Home')}
          >
            <MaterialCommunityIcons name="logout" size={20} color="#ef4444" />
            <Text style={styles.logoutText}>Logout</Text>
          </TouchableOpacity>
          
          <Text style={styles.versionText}>v2.1.0 • Engineering Learning Hub</Text>
        </View>
      </Animated.View>
    </>
  );

  const syncOfflineData = async () => {
    try {
      const offlineActions = await AsyncStorage.getItem('offlineActions');
      if (offlineActions) {
        const actions = JSON.parse(offlineActions);
        console.log('Syncing offline actions:', actions.length);
        await AsyncStorage.removeItem('offlineActions');
      }
    } catch (error) {
      console.error('Error syncing offline data:', error);
    }
  };

  const loadUserData = async () => {
    try {
      // The user object may be saved under any of several keys depending on
      // which login screen was used. We collect all candidates and pick the
      // best one — preferring an object that has a real email AND a full_name
      // (the actual logged-in account) over a stored display-name-only object
      // (e.g. an onboarding/demo placeholder).
      const keys = ['user_data', 'user', 'userData', 'currentUser', 'learningUser', 'authUser', 'profile', 'userProfile'];
      const pairs = await AsyncStorage.multiGet(keys);
      const candidates = [];
      for (const [, raw] of pairs) {
        if (!raw) continue;
        let parsed;
        try { parsed = JSON.parse(raw); } catch { continue; }
        if (!parsed || typeof parsed !== 'object') continue;
        const c = parsed.user && typeof parsed.user === 'object' ? parsed.user : parsed;
        const realName = c.full_name || c.fullName || c.name || c.first_name || c.firstName;
        if (!realName && !c.email) continue;
        candidates.push(c);
      }
      // Score: real email + a name from the login API beats a placeholder.
      const score = (c) => {
        let s = 0;
        if (c.email && /@/.test(String(c.email))) s += 2;
        if (c.full_name || c.fullName) s += 2;
        if (c.id) s += 1;
        // demote obvious placeholders
        const nm = String(c.full_name || c.fullName || c.name || '').toLowerCase();
        if (nm.includes('demo') || nm.includes('review') || nm.includes('test')) s -= 5;
        return s;
      };
      candidates.sort((a, b) => score(b) - score(a));
      if (candidates.length) setUserData(candidates[0]);
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadLiveCourses = async () => {
    try {
      const mockLiveCourses = [
        {
          id: 'LVC3',
          title: '🔴 LIVE NOW: CAD Design Challenge',
          subtitle: 'Real-time 3D modeling competition',
          instructor: 'Eng. Mohammed Ali',
          duration: '2 hours',
          enrolled: 94,
          status: 'live',
          color: '#FF6B35',
          videoUrl: 'https://teams.microsoft.com/l/meetup-join/19:meeting_xyz',
          roomPassword: 'CAD2024',
          startTime: new Date(),
          endTime: new Date(Date.now() + 2 * 60 * 60 * 1000),
          thumbnail: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400',
          category: 'mechanical',
          isInteractive: true,
          hasWhiteboard: true,
          hasBreakoutRooms: true,
        },
        {
          id: 'LVC1',
          title: 'Structural Analysis Workshop',
          subtitle: 'Live interactive workshop',
          instructor: 'Dr. Ahmed Hassan',
          duration: '2 hours',
          enrolled: 156,
          status: 'upcoming',
          color: '#0e4e96',
          price: 49,
          startTime: new Date(Date.now() + 3 * 60 * 60 * 1000),
          videoUrl: 'https://meet.google.com/xyz-live-structural',
          thumbnail: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=400',
          category: 'civil',
          isInteractive: true,
          hasWhiteboard: true,
        },
        {
          id: 'LVC2',
          title: 'Python Coding Marathon',
          subtitle: '6-hour live coding session',
          instructor: 'Prof. Sarah Johnson',
          duration: '6 hours',
          enrolled: 289,
          status: 'upcoming',
          color: '#3776AB',
          isFree: true,
          price: 0,
          startTime: new Date(Date.now() + 24 * 60 * 60 * 1000),
          videoUrl: 'https://zoom.us/j/123456789',
          thumbnail: 'https://images.unsplash.com/photo-1526379879527-8559ecfcaec7?w-400',
          category: 'computer',
          isInteractive: true,
          hasBreakoutRooms: true,
        },
      ];
      
      const rows = await fetchLearnTable('live_sessions');
      const mapped = rows.map((r) => ({
        ...mapCourseRow(r, '#E91E63', 'video'),
        isLive: r.status === 'live' || r.is_live === true,
        scheduledAt: r.scheduled_at || r.start_time || r.session_date || null,
        sessionUrl: sStr(r.session_url || r.meeting_url || r.join_url, ''),
      }));
      if (mapped.length) {
        setLiveCourses(mapped);
        try { await AsyncStorage.setItem('cache_liveCourses', JSON.stringify(mapped)); } catch {}
      } else {
        const cached = await AsyncStorage.getItem('cache_liveCourses');
        setLiveCourses(cached ? JSON.parse(cached) : mockLiveCourses);
      }
      await scheduleLiveSessionNotifications(mockLiveCourses);
    } catch (error) {
      console.error('Error loading live courses:', error);
      throw error;
    }
  };

  const loadCurrentCourses = async () => {
    try {
      const mockCourses = [
        {
          id: '1',
          title: 'Introduction to Python for Engineers',
          subtitle: 'Programming fundamentals for engineering applications',
          instructor: 'Prof. Sarah Johnson',
          duration: '8 hours',
          rating: 4.8,
          students: 18500,
          level: 'Beginner',
          isFree: true,
          price: 0,
          color: '#3776AB',
          icon: 'language-python',
          progress: 65,
          category: 'computer',
          description: 'Learn Python programming fundamentals with engineering applications.',
          modules: [
            { id: '1', title: 'Python Basics and Syntax', duration: '2 hours', completed: true },
            { id: '2', title: 'Data Structures for Engineering', duration: '2 hours', completed: true },
            { id: '3', title: 'NumPy and Scientific Computing', duration: '2 hours', completed: false },
            { id: '4', title: 'Engineering Applications', duration: '2 hours', completed: false },
          ],
          videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
          thumbnail: 'https://images.unsplash.com/photo-1526379879527-8559ecfcaec7?w=400',
          lastAccessed: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        },
        {
          id: '5',
          title: 'Advanced Structural Analysis',
          subtitle: 'Finite element methods and dynamic analysis',
          instructor: 'Dr. Robert Williams',
          duration: '25 hours',
          rating: 4.9,
          students: 3200,
          level: 'Advanced',
          isFree: false,
          price: 149,
          color: '#0e4e96',
          icon: 'bridge',
          progress: 40,
          category: 'civil',
          description: 'Master advanced structural analysis techniques.',
          modules: [
            { id: '1', title: 'FEM Fundamentals', duration: '5 hours', completed: true },
            { id: '2', title: 'Dynamic Analysis', duration: '6 hours', completed: true },
            { id: '3', title: 'Seismic Design Principles', duration: '7 hours', completed: false },
            { id: '4', title: 'Advanced Applications', duration: '7 hours', completed: false },
          ],
          videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4',
          thumbnail: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=400',
          lastAccessed: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        },
        {
          id: '6',
          title: 'Machine Learning for Engineers',
          subtitle: 'AI applications in engineering systems',
          instructor: 'Dr. Alex Rodriguez',
          duration: '30 hours',
          rating: 4.8,
          students: 4500,
          level: 'Intermediate',
          isFree: false,
          price: 199,
          color: '#FF4081',
          icon: 'robot',
          progress: 80,
          category: 'computer',
          description: 'Apply machine learning techniques to solve complex engineering problems.',
          modules: [
            { id: '1', title: 'ML Fundamentals for Engineers', duration: '6 hours', completed: true },
            { id: '2', title: 'Data Preprocessing', duration: '6 hours', completed: true },
            { id: '3', title: 'Predictive Modeling', duration: '9 hours', completed: true },
            { id: '4', title: 'Engineering Case Studies', duration: '9 hours', completed: false },
          ],
          videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4',
          thumbnail: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400',
          lastAccessed: new Date().toISOString(),
        },
      ];
      
      const rows = await fetchLearnTable('courses');
      const mapped = rows.map((r) => mapCourseRow(r, '#0e4e96', 'book-open-variant'));
      // Prefer enrolled/in-progress courses if the table flags them; else show all.
      const inProgress = mapped.filter((c) => sNum(c.progress) > 0);
      const list = (inProgress.length ? inProgress : mapped);
      if (list.length) {
        setCurrentCourses(list);
        setFilteredCourses(list);
        try { await AsyncStorage.setItem('cache_currentCourses', JSON.stringify(list)); } catch {}
        setDbConnected(true);
      } else {
        const cached = await AsyncStorage.getItem('cache_currentCourses');
        const fallback = cached ? JSON.parse(cached) : mockCourses;
        setCurrentCourses(fallback);
        setFilteredCourses(fallback);
        setDbConnected(false);
      }
    } catch (error) {
      console.error('Error loading current courses:', error);
      throw error;
    }
  };

  const loadFreeCourses = async () => {
    try {
      const mockFreeCourses = [
        {
          id: 'FREE1',
          title: 'Mechanical Engineering Design',
          subtitle: 'CAD, SolidWorks, Engineering Drawing - MIT Course',
          instructor: 'MIT OpenCourseWare',
          duration: '60 hours',
          rating: 4.9,
          students: 185400,
          level: 'Intermediate',
          isFree: true,
          price: 0,
          color: '#FF6B35',
          icon: 'cog',
          progress: 0,
          category: 'mechanical',
          description: 'Complete mechanical engineering design course from MIT.',
          modules: [
            { id: '1', title: 'Engineering Graphics', duration: '15 hours', completed: false },
            { id: '2', title: 'SolidWorks Basics', duration: '20 hours', completed: false },
            { id: '3', title: 'Advanced CAD', duration: '25 hours', completed: false },
          ],
          videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
          thumbnail: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400',
          platform: 'MIT OpenCourseWare',
          certificateAvailable: false,
          enrollmentLink: 'https://ocw.mit.edu/courses/2-007-design-and-manufacturing-i-spring-2009/',
        },
      ];
      
      const rows = await fetchLearnTable('courses', 'is_free=true');
      const mapped = rows
        .map((r) => mapCourseRow(r, '#FF6B35', 'cog'))
        .filter((c) => c.isFree);
      if (mapped.length) {
        setFreeCourses(mapped);
        try { await AsyncStorage.setItem('cache_freeCourses', JSON.stringify(mapped)); } catch {}
      } else {
        const cached = await AsyncStorage.getItem('cache_freeCourses');
        setFreeCourses(cached ? JSON.parse(cached) : mockFreeCourses);
      }
    } catch (error) {
      console.error('Error loading free courses:', error);
      throw error;
    }
  };

  const loadBookmarks = async () => {
    try {
      const bookmarks = await AsyncStorage.getItem('bookmarkedCourses');
      if (bookmarks) {
        setBookmarkedCourses(JSON.parse(bookmarks));
      }
    } catch (error) {
      console.error('Error loading bookmarks:', error);
    }
  };

  const loadRatings = async () => {
    try {
      const ratings = await AsyncStorage.getItem('courseRatings');
      if (ratings) {
        setRatedCourses(JSON.parse(ratings));
      }
    } catch (error) {
      console.error('Error loading ratings:', error);
    }
  };

  const loadChatHistory = async () => {
    try {
      const storedMessages = await AsyncStorage.getItem('chatHistory');
      if (storedMessages) {
        const messages = JSON.parse(storedMessages);
        if (messages.length > 0) {
          setChatMessages(messages);
        } else {
          setChatMessages([
            {
              id: 1,
              text: "🤖 Hello! I'm your AI Learning Assistant. I can help you with course recommendations, progress analysis, study planning, and career guidance. What would you like to know?",
              isAI: true,
              timestamp: new Date(),
            }
          ]);
        }
      } else {
        setChatMessages([
          {
            id: 1,
            text: "🤖 Hello! I'm your AI Learning Assistant. I can help you with course recommendations, progress analysis, study planning, and career guidance. What would you like to know?",
            isAI: true,
            timestamp: new Date(),
          }
        ]);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const loadDarkModePreference = async () => {
    try {
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
    } catch (error) {
      console.error('Error loading dark mode preference:', error);
    }
  };

  const scheduleLiveSessionNotifications = async (courses) => {
    try {
      for (const course of courses) {
        if (course.status === 'upcoming') {
          const triggerTime = new Date(course.startTime.getTime() - 15 * 60 * 1000);
          const now = new Date();
          
          if (triggerTime > now) {
            await Notifications.scheduleNotificationAsync({
              content: {
                title: `🎥 Live Session Starting Soon!`,
                body: `${course.title} starts in 15 minutes`,
                data: { courseId: course.id, type: 'live-session-reminder' },
                sound: 'default',
              },
              trigger: {
                type: 'date',
                date: triggerTime,
              },
            });
            
            console.log(`Scheduled notification for course: ${course.title} at ${triggerTime}`);
          }
        }
      }
    } catch (error) {
      console.error('Error scheduling notifications:', error);
    }
  };

  const filterCourses = () => {
    let filtered = currentCourses;
    
    if (searchQuery.trim()) {
      filtered = filtered.filter(course =>
        String(course.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(course.subtitle ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(course.instructor ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(course.category ?? '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(course => course.category === selectedCategory);
    }
    
    setFilteredCourses(filtered);
  };

  const handleSearch = (text) => {
    setSearchQuery(text);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      filterCourses();
    }, 300);
  };

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const getUserName = () => {
    if (!userData) return 'Student';
    
    const fullName = userData.full_name || userData.fullName || userData.name;
    if (fullName) return fullName;
    
    const firstName = userData.first_name || userData.firstName || '';
    const lastName = userData.last_name || userData.lastName || '';
    const combinedName = `${firstName} ${lastName}`.trim();
    
    return combinedName || 'Student';
  };

  const getUserEducation = () => {
    if (!userData) return '';
    return userData.education_level || userData.educationLevel || userData.education || '';
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) {
      return;
    }

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      isAI: false,
      timestamp: new Date(),
    };

    setChatMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    await storeChatMessage(userMessage);

    try {
      setTimeout(() => {
        const aiResponse = generateAIResponse(inputMessage);
        const aiMessage = {
          id: Date.now() + 1,
          text: aiResponse,
          isAI: true,
          timestamp: new Date(),
        };
        setChatMessages(prev => [...prev, aiMessage]);
        storeChatMessage(aiMessage);
        setIsTyping(false);
      }, 1500);
    } catch (error) {
      console.error('Error getting AI response:', error);
      setIsTyping(false);
    }
  };

  const storeChatMessage = async (message) => {
    try {
      const storedMessages = await AsyncStorage.getItem('chatHistory') || '[]';
      const messages = JSON.parse(storedMessages);
      messages.push(message);
      await AsyncStorage.setItem('chatHistory', JSON.stringify(messages.slice(-100)));
    } catch (error) {
      console.error('Error storing chat message:', error);
    }
  };

  const generateAIResponse = (message) => {
    const lowerMessage = message.toLowerCase();
    const userName = getUserName();
    
    if (lowerMessage.includes('free') || lowerMessage.includes('online courses')) {
      const freeCourseList = freeCourses.slice(0, 3).map(course => 
        `• **${course.title}**\n  🎓 ${course.platform} • ⭐ ${course.rating}/5\n  ⏰ ${course.duration} • 👥 ${course.students.toLocaleString()} students`
      ).join('\n\n');
      
      return `🎯 **Free Online Engineering Courses**:\n\n${freeCourseList}\n\n✨ **All courses are completely FREE with**:\n• No hidden fees\n• Lifetime access\n• Certificate of completion (most courses)\n• Community support\n\n💡 **Pro Tip**: Bookmark courses you're interested in to access them easily later!`;
    }
    
    return `🤖 Hi ${userName}! I understand you're asking about "${message}". \n\n💡 **I can help you with**:\n• 📚 Course recommendations\n• 🎥 Live session guidance\n• 📊 Progress analysis & insights\n• 📅 Study planning & scheduling\n• 🚀 Career guidance & job market\n• 💡 Learning support & resources\n• 🎯 Skill development strategies\n• 💰 Budget-friendly options\n• 🎨 Interactive tools (whiteboard, etc.)\n• 👥 Group learning & breakout rooms\n\nCould you be more specific about what you'd like to know? I'm here to help optimize your learning journey and achieve your engineering goals! 🌟\n\nTry asking:\n• "What free courses are available?"\n• "How is my progress?"\n• "Help me plan my studies"\n• "What career options do I have?"\n• "Tell me about interactive features"`;
  };

  const handleLogout = () => {
    const userName = getUserName();
    Alert.alert(
      'Logout',
      `Are you sure you want to logout, ${userName}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Logout',
          style: 'destructive',
          onPress: async () => {
            try {
              await AsyncStorage.multiRemove(['user', 'userData', 'chatHistory']);
              navigation.reset({
                index: 0,
                routes: [{ name: 'Home' }],
              });
            } catch (error) {
              console.error('Error during logout:', error);
            }
          },
        },
      ]
    );
  };

  const toggleBookmark = async (courseId) => {
    try {
      let newBookmarks;
      if (bookmarkedCourses.includes(courseId)) {
        newBookmarks = bookmarkedCourses.filter(id => id !== courseId);
      } else {
        newBookmarks = [...bookmarkedCourses, courseId];
      }
      
      setBookmarkedCourses(newBookmarks);
      await AsyncStorage.setItem('bookmarkedCourses', JSON.stringify(newBookmarks));
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  const rateCourse = async (courseId, rating) => {
    try {
      const newRatings = { ...ratedCourses, [courseId]: rating };
      setRatedCourses(newRatings);
      await AsyncStorage.setItem('courseRatings', JSON.stringify(newRatings));
    } catch (error) {
      console.error('Error rating course:', error);
    }
  };

  const shareCourse = async (course) => {
    try {
      await Share.share({
        title: course.title,
        message: `Check out this engineering course: ${course.title}\n\n${course.subtitle}\n\nAvailable on our learning platform!`,
      });
    } catch (error) {
      console.error('Error sharing course:', error);
    }
  };

  const addToCalendar = async (course) => {
    try {
      const { status } = await Calendar.requestCalendarPermissionsAsync();
      if (status === 'granted') {
        const calendars = await Calendar.getCalendarsAsync(Calendar.EntityTypes.EVENT);
        const defaultCalendar = calendars.find(c => c.isPrimary) || calendars[0];
        
        if (defaultCalendar) {
          await Calendar.createEventAsync(defaultCalendar.id, {
            title: course.title,
            startDate: course.startTime,
            endDate: course.endTime || new Date(course.startTime.getTime() + 2 * 60 * 60 * 1000),
            location: 'Online',
            notes: course.subtitle,
            url: course.videoUrl,
          });
        }
      }
    } catch (error) {
      console.error('Error adding to calendar:', error);
    }
  };

  const playVideoPreview = (videoUrl) => {
    setSelectedVideoUrl(videoUrl);
    setVideoModalVisible(true);
  };

  const toggleDarkMode = async () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    await AsyncStorage.setItem('darkMode', JSON.stringify(newDarkMode));
  };

  const openCourseLink = async (course) => {
    try {
      if (!isOnline) {
        Alert.alert('Offline', 'You need to be online to access this course.');
        return;
      }
      
      await Linking.openURL(course.enrollmentLink);
    } catch (error) {
      console.error('Error opening course link:', error);
      Alert.alert('Error', 'Failed to open course link.');
    }
  };

  const categories = [
    { id: 'all', label: 'All', icon: 'grid' },
    { id: 'computer', label: 'Computer', icon: 'laptop' },
    { id: 'civil', label: 'Civil', icon: 'bridge' },
    { id: 'mechanical', label: 'Mechanical', icon: 'cog' },
    { id: 'electrical', label: 'Electrical', icon: 'flash' },
    { id: 'chemical', label: 'Chemical', icon: 'flask' },
  ];

  const gradients = darkMode ? {
    primary: ['#2d3748', '#4a5568'],
    secondary: ['#805ad5', '#d53f8c'],
    success: ['#38a169', '#68d391'],
    warning: ['#d69e2e', '#ecc94b'],
    danger: ['#e53e3e', '#fc8181'],
    info: ['#3182ce', '#63b3ed'],
    purple: ['#805ad5', '#b794f4'],
    orange: ['#dd6b20', '#f6ad55'],
    blue: ['#2b6cb0', '#4299e1'],
    green: ['#38a169', '#68d391'],
    pink: ['#d53f8c', '#fbb6ce'],
    ai: ['#4c51bf', '#667eea'],
    robot: ['#2b6cb0', '#4299e1'],
    offline: ['#718096', '#a0aec0'],
  } : {
    primary: ['#667eea', '#764ba2'],
    secondary: ['#f093fb', '#f5576c'],
    success: ['#4facfe', '#00f2fe'],
    warning: ['#43e97b', '#38f9d7'],
    danger: ['#fa709a', '#fee140'],
    info: ['#a8edea', '#fed6e3'],
    purple: ['#d299c2', '#fef9d7'],
    orange: ['#fdbb2d', '#22c1c3'],
    blue: ['#2196f3', '#21cbf3'],
    green: ['#56ab2f', '#a8e6cf'],
    pink: ['#ff9a9e', '#fecfef'],
    teal: ['#667eea', '#764ba2'],
    ai: ['#667eea', '#764ba2'],
    robot: ['#4facfe', '#00f2fe'],
    offline: ['#6b7280', '#9ca3af'],
  };

  const styles = getStyles(darkMode);

  const renderOfflineBanner = () => (
    !isOnline && (
      <View style={styles.offlineBanner} testID={testIDs.offlineBanner}>
        <MaterialCommunityIcons name="wifi-off" size={20} color="#ffffff" />
        <Text style={styles.offlineText}>Offline Mode - Some features may be limited</Text>
        <TouchableOpacity onPress={refreshData}>
          <Text style={styles.reconnectText}>Retry Connection</Text>
        </TouchableOpacity>
      </View>
    )
  );

  const renderLoading = () => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size={36} color={darkMode ? "#667eea" : "#667eea"} />
      <Text style={styles.loadingText}>Loading your learning dashboard...</Text>
    </View>
  );

  const renderError = () => (
    <View style={styles.errorContainer}>
      <MaterialCommunityIcons name="alert-circle-outline" size={64} color="#ef4444" />
      <Text style={styles.errorText}>{error}</Text>
      <TouchableOpacity style={styles.retryButton} onPress={loadInitialData}>
        <Text style={styles.retryButtonText}>Retry</Text>
      </TouchableOpacity>
    </View>
  );

  const renderSearchBar = () => (
    <View style={styles.searchContainer}>
      <TouchableOpacity style={styles.searchIcon}>
        <MaterialCommunityIcons name="magnify" size={24} color={darkMode ? "#cbd5e0" : "#6b7280"} />
      </TouchableOpacity>
      <TextInput
        style={styles.searchInput}
        placeholder="Search courses, instructors, topics..."
        value={searchQuery}
        onChangeText={handleSearch}
        placeholderTextColor={darkMode ? "#a0aec0" : "#6b7280"}
        testID={testIDs.searchInput}
      />
      {searchQuery.length > 0 && (
        <TouchableOpacity style={styles.clearButton} onPress={() => setSearchQuery('')}>
          <MaterialCommunityIcons name="close-circle" size={20} color={darkMode ? "#cbd5e0" : "#6b7280"} />
        </TouchableOpacity>
      )}
    </View>
  );

  const renderCategoryFilter = () => (
    <ScrollView 
      horizontal 
      showsHorizontalScrollIndicator={false} 
      style={styles.categoryContainer}
    >
      {categories.map((category) => (
        <TouchableOpacity
          key={category.id}
          style={[
            styles.categoryButton,
            selectedCategory === category.id && styles.categoryButtonActive,
          ]}
          onPress={() => setSelectedCategory(category.id)}
          testID={`${testIDs.categoryFilter}${category.id}`}
        >
          <MaterialCommunityIcons 
            name={category.icon} 
            size={16} 
            color={selectedCategory === category.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#6b7280')} 
          />
          <Text style={[
            styles.categoryText,
            selectedCategory === category.id && styles.categoryTextActive,
          ]}>
            {category.label}
          </Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  const renderVideoModal = () => (
    <Modal
      visible={videoModalVisible}
      animationType="slide"
      onRequestClose={() => setVideoModalVisible(false)}
    >
      <SafeAreaView style={styles.videoModalContainer}>
        <View style={styles.videoHeader}>
          <TouchableOpacity onPress={() => setVideoModalVisible(false)}>
            <MaterialCommunityIcons name="close" size={24} color="#ffffff" />
          </TouchableOpacity>
          <Text style={styles.videoTitle}>Course Preview</Text>
          <View style={{ width: 24 }} />
        </View>
        <Video
          ref={videoRef}
          source={selectedVideoUrl ? { uri: selectedVideoUrl } : undefined}
          style={styles.videoPlayer}
          useNativeControls
          resizeMode="contain"
          isLooping={false}
          onPlaybackStatusUpdate={(status) => {
            setIsPlaying(status.isPlaying);
          }}
        />
      </SafeAreaView>
    </Modal>
  );

  const renderWelcomeSection = () => (
    <Animated.View 
      style={[
        styles.welcomeSection,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
      testID={testIDs.welcomeSection}
    >
      <LinearGradient
        colors={gradients.primary}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.welcomeGradient}
      >
        <View style={styles.welcomeHeader}>
          <View style={styles.userInfo}>
            <Text style={styles.welcomeTitle}>✨ Welcome back,</Text>
            <Text style={styles.userName}>{getUserName()}! 🎓</Text>
            {getUserEducation() && (
              <Text style={styles.userEducation}>📚 {getUserEducation()}</Text>
            )}
            <View style={styles.onlineIndicator}>
              <View style={[styles.onlineDot, { backgroundColor: isOnline ? '#4ade80' : '#ef4444' }]} />
              <Text style={styles.onlineText}>{isOnline ? 'Online' : 'Offline'}</Text>
            </View>
          </View>
          <View style={styles.headerActions}>
            <TouchableOpacity
              style={styles.darkModeButton}
              onPress={toggleDarkMode}
              testID={testIDs.darkModeToggle}
            >
              <MaterialCommunityIcons 
                name={darkMode ? "weather-sunny" : "weather-night"} 
                size={24} 
                color="#ffffff" 
              />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.profileButton}
              onPress={() => navigation.navigate('LearningProfileScreen')}
            >
              <LinearGradient
                colors={gradients.orange}
                style={styles.avatarGradient}
              >
                <Text style={styles.avatarText}>
                  {getUserName().charAt(0).toUpperCase()}
                </Text>
              </LinearGradient>
            </TouchableOpacity>
          </View>
        </View>
        
        <Text style={styles.welcomeSubtitle}>
          🚀 Continue your engineering learning journey
        </Text>
      </LinearGradient>
    </Animated.View>
  );

  const renderAIAssistantButton = () => (
    <Animated.View 
      style={[
        styles.aiButtonContainer,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <TouchableOpacity
        style={styles.aiButton}
        onPress={() => setShowAIChat(true)}
        testID={testIDs.aiButton}
      >
        <LinearGradient
          colors={gradients.ai}
          style={styles.aiButtonGradient}
        >
          <View style={styles.aiButtonContent}>
            <LinearGradient
              colors={gradients.robot}
              style={styles.aiIcon}
            >
              <MaterialCommunityIcons name="robot-excited" size={32} color="#ffffff" />
            </LinearGradient>
            <View style={styles.aiButtonText}>
              <Text style={styles.aiButtonTitle}>🤖 Ask AI Assistant</Text>
              <Text style={styles.aiButtonSubtitle}>
                Get instant help with courses, live sessions & career guidance
              </Text>
            </View>
            <MaterialCommunityIcons name="chevron-right" size={24} color="#ffffff" />
          </View>
        </LinearGradient>
      </TouchableOpacity>
    </Animated.View>
  );

  const renderLearningStats = () => (
    <Animated.View 
      style={[
        styles.statsContainer,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
      testID={testIDs.statsSection}
    >
      <Text style={styles.sectionTitle}>📊 Your Learning Progress</Text>
      
      <View style={styles.statsGrid}>
        <TouchableOpacity
          style={styles.statCard}
          onPress={() => navigation.navigate('LearningCourses')}
        >
          <LinearGradient
            colors={gradients.blue}
            style={styles.statGradient}
          >
            <MaterialCommunityIcons name="book-open-variant" size={28} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.statNumber}>4</Text>
          <Text style={styles.statLabel}>Active</Text>
          <Text style={styles.statSubLabel}>Courses</Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={styles.statCard}
          onPress={() => navigation.navigate('LearningCertificateScreen')}
        >
          <LinearGradient
            colors={gradients.purple}
            style={styles.statGradient}
          >
            <MaterialCommunityIcons name="certificate" size={28} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.statNumber}>3</Text>
          <Text style={styles.statLabel}>Certificates</Text>
          <Text style={styles.statSubLabel}>Earned</Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={styles.statCard}
          onPress={() => navigation.navigate('MyLearningHistoryScreen')}
        >
          <LinearGradient
            colors={gradients.success}
            style={styles.statGradient}
          >
            <MaterialCommunityIcons name="clock" size={28} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.statNumber}>68h</Text>
          <Text style={styles.statLabel}>Learning</Text>
          <Text style={styles.statSubLabel}>Total Hours</Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={styles.statCard}
          onPress={() => navigation.navigate('MyLearningHistoryScreen')}
        >
          <LinearGradient
            colors={gradients.danger}
            style={styles.statGradient}
          >
            <MaterialCommunityIcons name="fire" size={28} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.statNumber}>15</Text>
          <Text style={styles.statLabel}>Day Streak</Text>
          <Text style={styles.statSubLabel}>Current</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.overallProgressContainer}>
        <View style={styles.progressHeader}>
          <Text style={styles.progressTitle}>🎯 Overall Progress</Text>
          <Text style={styles.progressPercentage}>68%</Text>
        </View>
        <View style={styles.progressBarContainer}>
          <LinearGradient
            colors={gradients.primary}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={[styles.progressFill, { width: '68%' }]}
          />
        </View>
        <Text style={styles.progressSubtitle}>✅ 4 of 15 courses completed • £437 spent • 3 live sessions attended</Text>
      </View>
    </Animated.View>
  );

  const renderCourseCard = (course, index) => (
    <TouchableOpacity
      key={course.id}
      style={styles.courseCard}
      onPress={() => navigation.navigate('CourseDetails', { courseId: course.id, course })}
      testID={`${testIDs.courseCard}${index}`}
      accessibilityLabel={`Course: ${course.title}`}
      accessibilityHint="Double tap to view course details"
    >
      <View style={styles.courseCardHeader}>
        <LinearGradient
          colors={[course.color || gradients.primary[0], course.color || gradients.primary[1]]}
          style={styles.courseIconContainer}
        >
          <MaterialCommunityIcons name={course.icon} size={24} color="#ffffff" />
        </LinearGradient>
        <View style={styles.courseHeaderInfo}>
          <Text style={styles.courseTitle} numberOfLines={2}>{course.title}</Text>
          <Text style={styles.courseInstructor}>{course.instructor}</Text>
        </View>
        <TouchableOpacity 
          onPress={() => toggleBookmark(course.id)}
          style={styles.bookmarkButton}
        >
          <MaterialCommunityIcons 
            name={bookmarkedCourses.includes(course.id) ? "bookmark" : "bookmark-outline"} 
            size={20} 
            color={bookmarkedCourses.includes(course.id) ? "#667eea" : (darkMode ? "#a0aec0" : "#6b7280")} 
          />
        </TouchableOpacity>
      </View>
      
      <Text style={styles.courseSubtitle} numberOfLines={2}>{course.subtitle}</Text>
      
      <View style={styles.courseProgressContainer}>
        <View style={styles.progressBarContainer}>
          <LinearGradient
            colors={gradients.primary}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={[styles.progressFill, { width: `${course.progress}%` }]}
          />
        </View>
        <Text style={styles.courseProgressText}>{course.progress}%</Text>
      </View>
      
      <View style={styles.courseActions}>
        <TouchableOpacity 
          style={styles.courseActionButton}
          onPress={() => playVideoPreview(course.videoPreview)}
        >
          <MaterialCommunityIcons name="play-circle" size={20} color={darkMode ? "#a0aec0" : "#6b7280"} />
          <Text style={styles.courseActionText}>Preview</Text>
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={styles.courseActionButton}
          onPress={() => shareCourse(course)}
        >
          <MaterialCommunityIcons name="share-variant" size={20} color={darkMode ? "#a0aec0" : "#6b7280"} />
          <Text style={styles.courseActionText}>Share</Text>
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={styles.courseActionButton}
          onPress={() => rateCourse(course.id, 5)}
        >
          <MaterialCommunityIcons name="star" size={20} color={darkMode ? "#a0aec0" : "#6b7280"} />
          <Text style={styles.courseActionText}>Rate</Text>
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  const renderFreeCourseCard = (course, index) => (
    <TouchableOpacity
      key={course.id}
      style={styles.freeCourseCard}
      onPress={() => openCourseLink(course)}
      accessibilityLabel={`Free course: ${course.title}`}
      accessibilityHint="Double tap to enroll in this free course"
    >
      <View style={styles.freeCourseHeader}>
        <LinearGradient
          colors={[course.color, `${course.color}80`]}
          style={styles.freeCourseIconContainer}
        >
          <MaterialCommunityIcons name={course.icon} size={24} color="#ffffff" />
        </LinearGradient>
        <View style={styles.freeCourseTitleContainer}>
          <Text style={styles.freeCourseTitle} numberOfLines={2}>{course.title}</Text>
          <Text style={styles.freeCoursePlatform}>{course.platform}</Text>
        </View>
        <View style={styles.freeBadge}>
          <Text style={styles.freeBadgeText}>🎓 FREE</Text>
        </View>
      </View>
      
      <Text style={styles.freeCourseSubtitle} numberOfLines={2}>{course.subtitle}</Text>
      
      <View style={styles.freeCourseInfo}>
        <View style={styles.freeCourseMeta}>
          <MaterialCommunityIcons name="account" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
          <Text style={styles.freeCourseMetaText}>{course.instructor}</Text>
        </View>
        <View style={styles.freeCourseMeta}>
          <MaterialCommunityIcons name="clock-outline" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
          <Text style={styles.freeCourseMetaText}>{course.duration}</Text>
        </View>
        <View style={styles.freeCourseMeta}>
          <MaterialCommunityIcons name="star" size={14} color="#fbbf24" />
          <Text style={styles.freeCourseMetaText}>{course.rating}</Text>
          <Text style={styles.freeCourseStudents}>({course.students.toLocaleString()})</Text>
        </View>
      </View>
      
      {course.certificateAvailable && (
        <View style={styles.certificateBadge}>
          <MaterialCommunityIcons name="certificate" size={14} color="#10b981" />
          <Text style={styles.certificateText}>Certificate Available</Text>
        </View>
      )}
      
      <TouchableOpacity 
        style={styles.enrollButton}
        onPress={() => openCourseLink(course)}
      >
        <Text style={styles.enrollButtonText}>Enroll Now for Free</Text>
        <MaterialCommunityIcons name="open-in-new" size={16} color="#ffffff" />
      </TouchableOpacity>
    </TouchableOpacity>
  );

  const renderAIChatModal = () => (
    <Modal
      visible={showAIChat}
      animationType="slide"
      onRequestClose={() => setShowAIChat(false)}
    >
      <KeyboardAvoidingView 
        style={styles.chatModalContainer}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <LinearGradient
          colors={gradients.primary}
          style={[styles.chatHeader, { paddingTop: insets.top + 14 }]}
        >
          <TouchableOpacity
            style={styles.chatCloseButton}
            onPress={() => setShowAIChat(false)}
            hitSlop={{ top: 15, bottom: 15, left: 15, right: 15 }}
          >
            <MaterialCommunityIcons name="close" size={24} color="#ffffff" />
          </TouchableOpacity>
          <Text style={styles.chatHeaderTitle}>🤖 AI Learning Assistant</Text>
          <View style={styles.chatHeaderSpacer} />
        </LinearGradient>

        <FlatList
          data={chatMessages}
          keyExtractor={(item, index) => String(item?.id ?? index)}
          style={styles.chatMessages}
          renderItem={({ item }) => (
            <View style={[
              styles.messageContainer,
              item.isAI ? styles.aiMessageContainer : styles.userMessageContainer
            ]}>
              {item.isAI && (
                <LinearGradient
                  colors={gradients.robot}
                  style={styles.aiAvatar}
                >
                  <MaterialCommunityIcons name="robot" size={16} color="#ffffff" />
                </LinearGradient>
              )}
              <View style={[
                styles.messageBubble,
                item.isAI ? styles.aiMessageBubble : styles.userMessageBubble
              ]}>
                <Text style={[
                  styles.messageText,
                  item.isAI ? styles.aiMessageText : styles.userMessageText
                ]}>
                  {item.text}
                </Text>
                <Text style={styles.messageTime}>
                  {formatMsgTime(item.timestamp)}
                </Text>
              </View>
              {!item.isAI && (
                <View style={styles.userAvatar}>
                  <MaterialCommunityIcons name="account" size={16} color={darkMode ? "#4a5568" : "#6b7280"} />
                </View>
              )}
            </View>
          )}
          ListFooterComponent={() => isTyping && (
            <View style={styles.typingIndicator}>
              <LinearGradient
                colors={gradients.robot}
                style={styles.aiAvatar}
              >
                <MaterialCommunityIcons name="robot" size={16} color="#ffffff" />
              </LinearGradient>
              <View style={styles.typingBubble}>
                <Text style={styles.typingText}>🤖 AI is typing...</Text>
              </View>
            </View>
          )}
        />
        
        <View style={styles.chatInputContainer}>
          <TextInput
            style={styles.chatInput}
            placeholder="💬 Ask me anything about your learning..."
            value={inputMessage}
            onChangeText={setInputMessage}
            multiline
            placeholderTextColor={darkMode ? "#a0aec0" : "#6b7280"}
            editable={isOnline}
          />
          <TouchableOpacity
            style={styles.sendButton}
            onPress={sendMessage}
            disabled={!inputMessage.trim() || !isOnline}
          >
            <LinearGradient
              colors={inputMessage.trim() && isOnline ? gradients.primary : ['#e5e7eb', '#f3f4f6']}
              style={styles.sendButtonGradient}
            >
              <MaterialCommunityIcons 
                name="send" 
                size={20} 
                color={inputMessage.trim() && isOnline ? "#ffffff" : "#9ca3af"} 
              />
            </LinearGradient>
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );

  const renderUniversityCard = (university, index) => (
    <TouchableOpacity
      key={university.id}
      style={styles.universityCard}
      onPress={() => navigation.navigate('UniversityDetails', { university })}
      accessibilityLabel={`University: ${university.name}`}
      accessibilityHint="Double tap to view university details"
    >
      <LinearGradient
        colors={[university.color, `${university.color}80`]}
        style={styles.universityHeader}
      >
        <View style={styles.universityLogoContainer}>
          <MaterialCommunityIcons name={university.icon} size={32} color="#ffffff" />
        </View>
        <View style={styles.universityTitleContainer}>
          <Text style={styles.universityName} numberOfLines={2}>{university.name}</Text>
          <Text style={styles.universityLocation}>{university.location}</Text>
          <Text style={styles.universityRanking}>{university.ranking}</Text>
        </View>
      </LinearGradient>
      
      <View style={styles.universityContent}>
        <Text style={styles.universityDescription} numberOfLines={3}>
          {university.description}
        </Text>
        
        <View style={styles.universitySpecializations}>
          <Text style={styles.specializationsTitle}>Specializations:</Text>
          <View style={styles.specializationsList}>
            {university.specializations.slice(0, 3).map((spec, idx) => (
              <View key={idx} style={styles.specializationTag}>
                <Text style={styles.specializationText}>{spec}</Text>
              </View>
            ))}
            {university.specializations.length > 3 && (
              <View style={styles.moreTag}>
                <Text style={styles.moreText}>+{university.specializations.length - 3} more</Text>
              </View>
            )}
          </View>
        </View>
        
        <View style={styles.universityDetails}>
          <View style={styles.detailRow}>
            <MaterialCommunityIcons name="cash" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={styles.detailText}>UK: {university.tuition}</Text>
          </View>
          <View style={styles.detailRow}>
            <MaterialCommunityIcons name="calendar" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={styles.detailText}>Deadline: {university.applicationDeadline}</Text>
          </View>
        </View>
        
        <TouchableOpacity 
          style={styles.universityButton}
          onPress={() => Linking.openURL(university.website)}
        >
          <Text style={styles.universityButtonText}>Visit Website</Text>
          <MaterialCommunityIcons name="open-in-new" size={16} color="#ffffff" />
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  const renderBookCard = (book, index) => {
    const isFree = Number(book.price) === 0 || book.pdfAvailable === true || !!book.pdfUrl;
    return (
    <TouchableOpacity
      key={book.id}
      style={styles.bookCard}
      onPress={() => navigation.navigate('BookDetailScreen', { book })}
      accessibilityLabel={`Book: ${book.title}`}
      accessibilityHint="Double tap to view book details"
    >
      <LinearGradient
        colors={[book.color, `${book.color}80`]}
        style={styles.bookHeader}
      >
        <View style={styles.bookIconContainer}>
          <MaterialCommunityIcons name={book.icon} size={28} color="#ffffff" />
        </View>
        <View style={styles.bookTitleContainer}>
          <Text style={styles.bookTitle} numberOfLines={2}>{book.title}</Text>
          <Text style={styles.bookAuthor}>by {book.author}</Text>
        </View>
      </LinearGradient>
      
      <View style={styles.bookContent}>
        <Text style={styles.bookEdition}>{book.edition} • {book.publisher} ({book.year})</Text>
        
        <Text style={styles.bookDescription} numberOfLines={3}>
          {book.description}
        </Text>
        
        <View style={styles.bookDetails}>
          <View style={styles.bookDetailItem}>
            <MaterialCommunityIcons name="book-open-page-variant" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={styles.bookDetailText}>{book.pages} pages</Text>
          </View>
          <View style={styles.bookDetailItem}>
            <MaterialCommunityIcons name="star" size={14} color="#fbbf24" />
            <Text style={styles.bookDetailText}>{book.rating}/5</Text>
          </View>
          <View style={styles.bookDetailItem}>
            <MaterialCommunityIcons name="cash" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={styles.bookDetailText}>{book.price}</Text>
          </View>
        </View>
        
        {book.topics && (
          <View style={styles.bookTopics}>
            {book.topics.slice(0, 2).map((topic, idx) => (
              <View key={idx} style={styles.topicTag}>
                <Text style={styles.topicText}>{topic}</Text>
              </View>
            ))}
          </View>
        )}
        
        <View style={styles.bookActions}>
          {isFree ? (
            <TouchableOpacity
              style={[styles.bookActionButton, styles.pdfButton]}
              onPress={() => navigation.navigate(book.pdfUrl ? 'PDFViewer' : 'BookDetailScreen', { book })}
            >
              <MaterialCommunityIcons name="book-open-variant" size={16} color="#ffffff" />
              <Text style={styles.bookActionText}>Read</Text>
            </TouchableOpacity>
          ) : (
            book.amazonLink ? (
              <TouchableOpacity
                style={[styles.bookActionButton, styles.amazonButton]}
                onPress={() => Linking.openURL(book.amazonLink)}
              >
                <FontAwesome5 name="amazon" size={16} color="#ffffff" />
                <Text style={styles.bookActionText}>Amazon</Text>
              </TouchableOpacity>
            ) : null
          )}

          <TouchableOpacity
            style={[styles.bookActionButton, styles.previewButton]}
            onPress={() => navigation.navigate('BookPreview', { book })}
          >
            <MaterialCommunityIcons name="eye" size={16} color="#ffffff" />
            <Text style={styles.bookActionText}>Preview</Text>
          </TouchableOpacity>
        </View>
      </View>
    </TouchableOpacity>
    );
  };

  const renderTabNavigation = () => (
    <View style={styles.tabContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabScroll}>
        <TouchableOpacity
          style={[styles.tabButton, selectedTab === 'courses' && styles.tabButtonActive]}
          onPress={() => setSelectedTab('courses')}
        >
          <MaterialCommunityIcons 
            name="book-open-variant" 
            size={20} 
            color={selectedTab === 'courses' ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
          />
          <Text style={[
            styles.tabText,
            selectedTab === 'courses' && styles.tabTextActive
          ]}>Courses</Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[styles.tabButton, selectedTab === 'universities' && styles.tabButtonActive]}
          onPress={() => setSelectedTab('universities')}
        >
          <MaterialCommunityIcons 
            name="school" 
            size={20} 
            color={selectedTab === 'universities' ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
          />
          <Text style={[
            styles.tabText,
            selectedTab === 'universities' && styles.tabTextActive
          ]}>UK Universities</Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[styles.tabButton, selectedTab === 'books' && styles.tabButtonActive]}
          onPress={() => setSelectedTab('books')}
        >
          <MaterialCommunityIcons 
            name="book-open-page-variant" 
            size={20} 
            color={selectedTab === 'books' ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
          />
          <Text style={[
            styles.tabText,
            selectedTab === 'books' && styles.tabTextActive
          ]}>Books</Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[styles.tabButton, selectedTab === 'live' && styles.tabButtonActive]}
          onPress={() => setSelectedTab('live')}
        >
          <MaterialCommunityIcons 
            name="video" 
            size={20} 
            color={selectedTab === 'live' ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
          />
          <Text style={[
            styles.tabText,
            selectedTab === 'live' && styles.tabTextActive
          ]}>Live Sessions</Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[styles.tabButton, selectedTab === 'free' && styles.tabButtonActive]}
          onPress={() => setSelectedTab('free')}
        >
          <MaterialCommunityIcons 
            name="currency-usd-off" 
            size={20} 
            color={selectedTab === 'free' ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
          />
          <Text style={[
            styles.tabText,
            selectedTab === 'free' && styles.tabTextActive
          ]}>Free Courses</Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );

  const renderBookCategories = () => (
    <View style={styles.bookCategories}>
      <TouchableOpacity style={styles.categoryTag}>
        <Text style={styles.categoryTagText}>Mechanical</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.categoryTag}>
        <Text style={styles.categoryTagText}>Electrical</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.categoryTag}>
        <Text style={styles.categoryTagText}>Civil</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.categoryTag}>
        <Text style={styles.categoryTagText}>Chemical</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.categoryTag}>
        <Text style={styles.categoryTagText}>Computer</Text>
      </TouchableOpacity>
    </View>
  );

  const renderQuickStats = () => (
    <View style={styles.quickStats}>
      <View style={styles.quickStatItem}>
        <MaterialCommunityIcons name="school" size={24} color="#667eea" />
        <Text style={styles.quickStatNumber}>{ukUniversities.length}</Text>
        <Text style={styles.quickStatLabel}>UK Universities</Text>
      </View>
      <View style={styles.quickStatItem}>
        <MaterialCommunityIcons name="book-open" size={24} color="#667eea" />
        <Text style={styles.quickStatNumber}>{engineeringBooks.length}</Text>
        <Text style={styles.quickStatLabel}>Books</Text>
      </View>
      <View style={styles.quickStatItem}>
        <MaterialCommunityIcons name="video" size={24} color="#667eea" />
        <Text style={styles.quickStatNumber}>{liveCourses.length}</Text>
        <Text style={styles.quickStatLabel}>Live Sessions</Text>
      </View>
      <View style={styles.quickStatItem}>
        <MaterialCommunityIcons name="currency-usd-off" size={24} color="#667eea" />
        <Text style={styles.quickStatNumber}>{freeCourses.length}</Text>
        <Text style={styles.quickStatLabel}>Free Courses</Text>
      </View>
    </View>
  );

  if (loading) {
    return renderLoading();
  }

  if (error && !isRefreshing) {
    return renderError();
  }

  return (
    <SafeAreaView style={styles.safeArea} testID={testIDs.dashboard}>
      <StatusBar barStyle={darkMode ? "light-content" : "light-content"} backgroundColor="#667eea" />
      
      {renderSideMenu()}
      
      <LinearGradient
        colors={gradients.primary}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={styles.header}
      >
        <TouchableOpacity 
          style={styles.menuButton} 
          onPress={toggleMenu}
          accessibilityLabel="Menu"
          accessibilityHint="Double tap to open navigation menu"
        >
          <MaterialCommunityIcons name="menu" size={28} color="#ffffff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>🎓 Learning Dashboard</Text>
        <TouchableOpacity onPress={handleLogout} style={styles.logoutButton} testID={testIDs.logoutButton}>
          <MaterialCommunityIcons name="logout" size={28} color="#ffffff" />
        </TouchableOpacity>
      </LinearGradient>

      {dbConnected !== null && (
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={() => { setDbConnected(null); refreshData(); }}
          style={[
            styles.dbBadge,
            { backgroundColor: dbConnected ? '#E8F5E9' : '#FFF3E0' },
          ]}
        >
          <View style={[styles.dbDot, { backgroundColor: dbConnected ? '#2E7D32' : '#EF6C00' }]} />
          <Text style={[styles.dbBadgeText, { color: dbConnected ? '#2E7D32' : '#EF6C00' }]}>
            {dbConnected ? 'Live data · connected to JobEng' : 'Offline data · tap to retry'}
          </Text>
        </TouchableOpacity>
      )}

      {renderOfflineBanner()}
      {renderSearchBar()}
      {renderTabNavigation()}
      
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={refreshData}
            colors={['#667eea']}
            tintColor="#667eea"
            testID={testIDs.refreshControl}
          />
        }
      >
        {renderWelcomeSection()}
        {renderAIAssistantButton()}
        {renderLearningStats()}
        
        {selectedTab === 'courses' && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>📚 Continue Learning</Text>
            {filteredCourses.length > 0 ? (
              filteredCourses.map((course, index) => renderCourseCard(course, index))
            ) : (
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="book-open-variant" size={48} color={darkMode ? "#a0aec0" : "#6b7280"} />
                <Text style={styles.emptyStateText}>No courses found</Text>
                <Text style={styles.emptyStateSubtext}>
                  {searchQuery ? 'Try a different search term' : 'Explore available courses'}
                </Text>
              </View>
            )}
          </View>
        )}
        
        {selectedTab === 'universities' && (
          <View style={styles.section} testID={testIDs.ukUniversities}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>🎓 Top UK Engineering Universities</Text>
              <TouchableOpacity onPress={() => navigation.navigate('UKUniversitiesScreen')}>
                <Text style={styles.seeAllText}>View All</Text>
              </TouchableOpacity>
            </View>
            <Text style={styles.sectionSubtitle}>
              Top-ranked engineering universities in the United Kingdom with world-class programs
            </Text>
            
            <ScrollView 
              horizontal 
              showsHorizontalScrollIndicator={false}
              style={styles.horizontalScroll}
            >
              {ukUniversities.map((university, index) => renderUniversityCard(university, index))}
            </ScrollView>
            
            <View style={styles.universityStats}>
              <View style={styles.statItem}>
                <Text style={styles.statNumber}>8</Text>
                <Text style={styles.statLabel}>Top Universities</Text>
              </View>
              <View style={styles.statItem}>
                <Text style={styles.statNumber}>£9,250</Text>
                <Text style={styles.statLabel}>Avg UK Tuition</Text>
              </View>
              <View style={styles.statItem}>
                <Text style={styles.statNumber}>15 Jan</Text>
                <Text style={styles.statLabel}>Avg Deadline</Text>
              </View>
              <View style={styles.statItem}>
                <Text style={styles.statNumber}>A*AA</Text>
                <Text style={styles.statLabel}>Avg Entry</Text>
              </View>
            </View>
          </View>
        )}
        
        {selectedTab === 'books' && (
          <View style={styles.section} testID={testIDs.engineeringBooks}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>📚 Essential Engineering Books</Text>
              <TouchableOpacity onPress={() => navigation.navigate('EngineeringBooksScreen')}>
                <Text style={styles.seeAllText}>View All</Text>
              </TouchableOpacity>
            </View>
            <Text style={styles.sectionSubtitle}>
              Must-have textbooks and references for engineering students
            </Text>
            
            <ScrollView 
              horizontal 
              showsHorizontalScrollIndicator={false}
              style={styles.horizontalScroll}
            >
              {engineeringBooks.map((book, index) => renderBookCard(book, index))}
            </ScrollView>
            
            {renderBookCategories()}
          </View>
        )}
        
        {selectedTab === 'live' && (
          <View style={styles.section} testID={testIDs.liveCourses}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>🎥 Live Courses & Webinars</Text>
              <TouchableOpacity onPress={() => navigation.navigate('LiveVideoCoursesScreen')}>
                <Text style={styles.seeAllText}>View All</Text>
              </TouchableOpacity>
            </View>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.horizontalScroll}>
              {liveCourses.map((course, index) => (
                <TouchableOpacity
                  key={course.id}
                  style={styles.liveCourseCard}
                  onPress={() => navigation.navigate('LiveVideoSession', { course })}
                >
                  <LinearGradient
                    colors={[course.color, `${course.color}80`]}
                    style={styles.liveCourseHeader}
                  >
                    <View style={styles.liveBadge}>
                      <Text style={styles.liveBadgeText}>
                        {course.status === 'live' ? '🔴 LIVE NOW' : '⏰ UPCOMING'}
                      </Text>
                    </View>
                    <Text style={styles.liveCourseTitle}>{course.title}</Text>
                    <Text style={styles.liveCourseSubtitle}>{course.subtitle}</Text>
                  </LinearGradient>
                  <View style={styles.liveCourseInfo}>
                    <View style={styles.liveCourseMeta}>
                      <MaterialCommunityIcons name="account" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                      <Text style={styles.liveCourseMetaText}>{course.instructor}</Text>
                    </View>
                    <View style={styles.liveCourseMeta}>
                      <MaterialCommunityIcons name="clock" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                      <Text style={styles.liveCourseMetaText}>{course.duration}</Text>
                    </View>
                    <View style={styles.liveCourseMeta}>
                      <MaterialCommunityIcons name="account-group" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                      <Text style={styles.liveCourseMetaText}>{course.enrolled} enrolled</Text>
                    </View>
                  </View>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        )}
        
        {selectedTab === 'free' && (
          <View style={styles.section} testID={testIDs.freeCourses}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>🎯 Free Online Courses</Text>
              <TouchableOpacity onPress={() => navigation.navigate('FreeCourses')}>
                <Text style={styles.seeAllText}>View All</Text>
              </TouchableOpacity>
            </View>
            <Text style={styles.sectionSubtitle}>
              Top-rated free engineering courses from leading platforms
            </Text>
            
            <ScrollView 
              horizontal 
              showsHorizontalScrollIndicator={false}
              style={styles.horizontalScroll}
            >
              {freeCourses.map((course, index) => renderFreeCourseCard(course, index))}
            </ScrollView>
          </View>
        )}
        
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>📈 Learning Recommendations</Text>
          <View style={styles.recommendationCard}>
            <MaterialCommunityIcons name="lightbulb-on" size={32} color="#f59e0b" />
            <View style={styles.recommendationContent}>
              <Text style={styles.recommendationTitle}>Based on your progress</Text>
              <Text style={styles.recommendationText}>
                {selectedTab === 'universities' 
                  ? 'Consider applying to University of Manchester for strong industry links'
                  : selectedTab === 'books'
                  ? 'Read "Introduction to Algorithms" to strengthen your programming skills'
                  : 'Complete "Python for Engineering Applications" next to unlock advanced ML courses'}
              </Text>
            </View>
          </View>
        </View>
        
        {renderQuickStats()}
        
        <View style={styles.bottomPadding} />
      </ScrollView>

      {renderVideoModal()}
      {renderAIChatModal()}
      
      <TouchableOpacity 
        style={styles.fab}
        onPress={() => setShowAIChat(true)}
        accessibilityLabel="Open AI Assistant"
        accessibilityRole="button"
      >
        <LinearGradient
          colors={gradients.ai}
          style={styles.fabGradient}
        >
          <MaterialCommunityIcons name="robot" size={24} color="#ffffff" />
        </LinearGradient>
      </TouchableOpacity>
    </SafeAreaView>
  );
};

const getStyles = (darkMode) => StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  menuOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#000000',
    zIndex: 999,
  },
  sideMenu: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: 300,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    zIndex: 1000,
    shadowColor: '#000',
    shadowOffset: { width: 2, height: 0 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 10,
  },
  menuHeader: {
    paddingTop: 60,
    paddingBottom: 30,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: darkMode ? '#4a5568' : 'rgba(255, 255, 255, 0.2)',
  },
  menuCloseButton: {
    position: 'absolute',
    top: 20,
    left: 20,
    padding: 8,
  },
  menuUserInfo: {
    alignItems: 'center',
  },
  menuAvatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  menuAvatarText: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  menuUserName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 4,
  },
  menuUserEmail: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  menuItems: {
    flex: 1,
    paddingVertical: 20,
  },
  menuSection: {
    marginBottom: 24,
  },
  menuSectionTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: darkMode ? '#a0aec0' : '#6b7280',
    paddingHorizontal: 20,
    marginBottom: 12,
    letterSpacing: 1,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderLeftWidth: 4,
    borderLeftColor: 'transparent',
  },
  menuItemActive: {
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    borderLeftColor: '#667eea',
  },
  menuIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  menuItemText: {
    flex: 1,
    fontSize: 16,
    color: darkMode ? '#f7fafc' : '#1f2937',
    fontWeight: '500',
  },
  badge: {
    backgroundColor: '#667eea',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    minWidth: 24,
    alignItems: 'center',
  },
  badgeText: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  menuFooter: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  logoutButtonMenu: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    paddingVertical: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  logoutText: {
    fontSize: 16,
    color: '#ef4444',
    fontWeight: '600',
    marginLeft: 8,
  },
  versionText: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    textAlign: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  menuButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ffffff',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  logoutButton: {
    padding: 8,
  },
  dbBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 14,
    marginTop: 8,
    marginBottom: 2,
  },
  dbDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 7,
  },
  dbBadgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  tabContainer: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  tabScroll: {
    flexDirection: 'row',
  },
  tabButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    marginRight: 8,
    borderWidth: 1,
    borderColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  tabButtonActive: {
    backgroundColor: '#667eea',
    borderColor: '#667eea',
  },
  tabText: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '500',
    color: darkMode ? '#cbd5e0' : '#6b7280',
  },
  tabTextActive: {
    color: '#ffffff',
  },
  universityCard: {
    width: 320,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 16,
    marginRight: 16,
    marginBottom: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  universityHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
  },
  universityLogoContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  universityTitleContainer: {
    flex: 1,
  },
  universityName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 4,
  },
  universityLocation: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: 4,
  },
  universityRanking: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    alignSelf: 'flex-start',
  },
  universityContent: {
    padding: 16,
  },
  universityDescription: {
    fontSize: 14,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginBottom: 16,
    lineHeight: 20,
  },
  universitySpecializations: {
    marginBottom: 16,
  },
  specializationsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 8,
  },
  specializationsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  specializationTag: {
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  specializationText: {
    fontSize: 12,
    color: darkMode ? '#cbd5e0' : '#4b5563',
  },
  moreTag: {
    backgroundColor: '#667eea',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  moreText: {
    fontSize: 12,
    color: '#ffffff',
  },
  universityDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  detailText: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginLeft: 6,
  },
  universityButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#667eea',
    paddingVertical: 12,
    borderRadius: 8,
  },
  universityButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginRight: 8,
  },
  universityStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    padding: 16,
    borderRadius: 12,
    marginTop: 16,
  },
  statItem: {
    alignItems: 'center',
  },
  statNumber: {
    fontSize: 18,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    textAlign: 'center',
  },
  bookCard: {
    width: 280,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 16,
    marginRight: 16,
    marginBottom: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  bookHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  bookIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  bookTitleContainer: {
    flex: 1,
  },
  bookTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 4,
  },
  bookAuthor: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  bookContent: {
    padding: 16,
  },
  bookEdition: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginBottom: 12,
  },
  bookDescription: {
    fontSize: 14,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginBottom: 16,
    lineHeight: 20,
  },
  bookDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  bookDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  bookDetailText: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginLeft: 4,
  },
  bookTopics: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  topicTag: {
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  topicText: {
    fontSize: 12,
    color: darkMode ? '#cbd5e0' : '#4b5563',
  },
  bookActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  bookActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    flex: 1,
    marginHorizontal: 4,
    justifyContent: 'center',
  },
  amazonButton: {
    backgroundColor: '#FF9900',
  },
  pdfButton: {
    backgroundColor: '#EF4444',
  },
  previewButton: {
    backgroundColor: '#667eea',
  },
  bookActionText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },
  bookCategories: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 16,
  },
  categoryTag: {
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  categoryTagText: {
    fontSize: 12,
    color: darkMode ? '#cbd5e0' : '#4b5563',
  },
  quickStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    padding: 16,
    borderRadius: 16,
    marginHorizontal: 20,
    marginTop: 16,
    marginBottom: 20,
  },
  quickStatItem: {
    alignItems: 'center',
    flex: 1,
  },
  quickStatNumber: {
    fontSize: 20,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginVertical: 8,
  },
  quickStatLabel: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    textAlign: 'center',
  },
  offlineBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#6b7280',
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  offlineText: {
    color: '#ffffff',
    marginLeft: 8,
    marginRight: 16,
    fontSize: 14,
  },
  reconnectText: {
    color: '#d1d5db',
    textDecorationLine: 'underline',
    fontSize: 14,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    marginHorizontal: 20,
    marginVertical: 12,
    borderRadius: 12,
    paddingHorizontal: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  searchIcon: {
    paddingRight: 12,
  },
  searchInput: {
    flex: 1,
    height: 48,
    fontSize: 16,
    color: darkMode ? '#f7fafc' : '#1f2937',
  },
  clearButton: {
    padding: 4,
  },
  categoryContainer: {
    paddingHorizontal: 20,
    marginBottom: 0,
    height: 50,
  },
  categoryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    marginRight: 8,
    borderWidth: 1,
    borderColor: darkMode ? '#4a5568' : '#e5e7eb',
    height: 40,
  },
  categoryButtonActive: {
    backgroundColor: '#667eea',
    borderColor: '#667eea',
  },
  categoryText: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '500',
    color: darkMode ? '#cbd5e0' : '#6b7280',
  },
  categoryTextActive: {
    color: '#ffffff',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
  },
  bottomPadding: {
    height: 80,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: darkMode ? '#cbd5e0' : '#6b7280',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
    paddingHorizontal: 32,
  },
  errorText: {
    fontSize: 16,
    color: darkMode ? '#cbd5e0' : '#374151',
    textAlign: 'center',
    marginVertical: 16,
  },
  retryButton: {
    backgroundColor: '#667eea',
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 8,
  },
  retryButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  section: {
    marginHorizontal: 20,
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 16,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginBottom: 16,
    lineHeight: 20,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  seeAllText: {
    fontSize: 14,
    color: '#667eea',
    fontWeight: '600',
  },
  welcomeSection: {
    marginHorizontal: 20,
    marginTop: 0,
    marginBottom: 16,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 8,
  },
  welcomeGradient: {
    padding: 24,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
  },
  welcomeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  userInfo: {
    flex: 1,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  darkModeButton: {
    padding: 8,
    marginRight: 8,
  },
  welcomeTitle: {
    fontSize: 18,
    color: '#ffffff',
    opacity: 0.9,
    marginBottom: 4,
  },
  userName: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 8,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  userEducation: {
    fontSize: 16,
    color: '#ffffff',
    opacity: 0.9,
    marginBottom: 12,
  },
  onlineIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  onlineDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 8,
  },
  onlineText: {
    fontSize: 14,
    color: '#ffffff',
    opacity: 0.9,
  },
  profileButton: {
    padding: 4,
  },
  avatarGradient: {
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  avatarText: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  welcomeSubtitle: {
    fontSize: 16,
    color: '#ffffff',
    opacity: 0.9,
    textAlign: 'center',
  },
  aiButtonContainer: {
    marginHorizontal: 20,
    marginBottom: 20,
  },
  aiButton: {
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 8,
  },
  aiButtonGradient: {
    padding: 20,
  },
  aiButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  aiIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  aiButtonText: {
    flex: 1,
  },
  aiButtonTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 6,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  aiButtonSubtitle: {
    fontSize: 14,
    color: '#ffffff',
    opacity: 0.9,
    lineHeight: 20,
  },
  statsContainer: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    marginHorizontal: 20,
    marginBottom: 20,
    padding: 24,
    borderRadius: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  statCard: {
    alignItems: 'center',
    flex: 1,
    padding: 8,
  },
  statGradient: {
    width: 60,
    height: 60,
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  statNumber: {
    fontSize: 22,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 14,
    color: darkMode ? '#cbd5e0' : '#1f2937',
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 2,
  },
  statSubLabel: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    textAlign: 'center',
  },
  overallProgressContainer: {
    marginTop: 8,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  progressTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: darkMode ? '#f7fafc' : '#374151',
  },
  progressPercentage: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#667eea',
  },
  progressBarContainer: {
    height: 10,
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    borderRadius: 5,
    marginBottom: 8,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 5,
  },
  progressSubtitle: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
    textAlign: 'center',
  },
  courseCard: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    marginBottom: 16,
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  courseCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  courseIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  courseHeaderInfo: {
    flex: 1,
  },
  courseTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  courseInstructor: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
  },
  bookmarkButton: {
    padding: 4,
  },
  courseSubtitle: {
    fontSize: 14,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginBottom: 16,
    lineHeight: 20,
  },
  courseProgressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  courseProgressText: {
    fontSize: 14,
    fontWeight: '600',
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginLeft: 12,
  },
  courseActions: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    borderTopWidth: 1,
    borderTopColor: darkMode ? '#4a5568' : '#e5e7eb',
    paddingTop: 16,
  },
  courseActionButton: {
    alignItems: 'center',
  },
  courseActionText: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginTop: 4,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 16,
  },
  emptyStateText: {
    fontSize: 18,
    fontWeight: '600',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginTop: 16,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginTop: 8,
    textAlign: 'center',
  },
  liveCourseCard: {
    width: 280,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 16,
    marginRight: 16,
    marginBottom: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  liveCourseHeader: {
    padding: 20,
    alignItems: 'center',
  },
  liveBadge: {
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    alignSelf: 'flex-start',
    marginBottom: 12,
  },
  liveBadgeText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  liveCourseTitle: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 4,
  },
  liveCourseSubtitle: {
    color: '#ffffff',
    fontSize: 14,
    opacity: 0.9,
    textAlign: 'center',
  },
  liveCourseInfo: {
    padding: 16,
  },
  liveCourseMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  liveCourseMetaText: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginLeft: 8,
  },
  freeCourseCard: {
    width: 280,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 16,
    marginRight: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  freeCourseHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  freeCourseIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  freeCourseTitleContainer: {
    flex: 1,
  },
  freeCourseTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  freeCoursePlatform: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
  },
  freeBadge: {
    backgroundColor: '#10b981',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  freeBadgeText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  freeCourseSubtitle: {
    fontSize: 14,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginBottom: 12,
    lineHeight: 20,
  },
  freeCourseInfo: {
    marginBottom: 12,
  },
  freeCourseMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  freeCourseMetaText: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginLeft: 6,
  },
  freeCourseStudents: {
    fontSize: 12,
    color: darkMode ? '#718096' : '#9ca3af',
    marginLeft: 4,
  },
  certificateBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#2d5e43' : '#d1fae5',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    marginBottom: 12,
    alignSelf: 'flex-start',
  },
  certificateText: {
    fontSize: 12,
    color: darkMode ? '#a7f3d0' : '#065f46',
    marginLeft: 4,
    fontWeight: '500',
  },
  enrollButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#667eea',
    paddingVertical: 12,
    borderRadius: 8,
  },
  enrollButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginRight: 8,
  },
  fab: {
    position: 'absolute',
    bottom: 24,
    right: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  fabGradient: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
  },
  videoModalContainer: {
    flex: 1,
    backgroundColor: '#000000',
  },
  videoHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#1f2937',
  },
  videoTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  videoPlayer: {
    flex: 1,
    backgroundColor: '#000000',
  },
  chatModalContainer: {
    flex: 1,
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  chatHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  chatCloseButton: {
    padding: 8,
  },
  chatHeaderTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  chatHeaderSpacer: {
    width: 40,
  },
  chatMessages: {
    flex: 1,
    padding: 16,
  },
  messageContainer: {
    flexDirection: 'row',
    marginBottom: 16,
    alignItems: 'flex-end',
  },
  aiMessageContainer: {
    justifyContent: 'flex-start',
  },
  userMessageContainer: {
    justifyContent: 'flex-end',
  },
  aiAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  userAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8,
  },
  messageBubble: {
    maxWidth: '75%',
    padding: 12,
    borderRadius: 16,
  },
  aiMessageBubble: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderBottomLeftRadius: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  userMessageBubble: {
    backgroundColor: '#667eea',
    borderBottomRightRadius: 4,
  },
  messageText: {
    fontSize: 15,
    lineHeight: 20,
    marginBottom: 4,
  },
  aiMessageText: {
    color: darkMode ? '#f7fafc' : '#1f2937',
  },
  userMessageText: {
    color: '#ffffff',
  },
  messageTime: {
    fontSize: 11,
    opacity: 0.7,
    color: darkMode ? '#a0aec0' : '#6b7280',
  },
  typingIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  typingBubble: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    padding: 12,
    borderRadius: 16,
    borderBottomLeftRadius: 4,
    marginLeft: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  typingText: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
    fontStyle: 'italic',
  },
  chatInputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    padding: 16,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderTopWidth: 1,
    borderTopColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  chatInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: darkMode ? '#4a5568' : '#d1d5db',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    maxHeight: 100,
    marginRight: 12,
    backgroundColor: darkMode ? '#1a202c' : '#f9fafb',
    color: darkMode ? '#f7fafc' : '#1f2937',
  },
  sendButton: {
    borderRadius: 20,
    overflow: 'hidden',
  },
  sendButtonGradient: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  recommendationCard: {
    flexDirection: 'row',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    padding: 16,
    borderRadius: 16,
    alignItems: 'center',
  },
  recommendationContent: {
    flex: 1,
    marginLeft: 16,
  },
  recommendationTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  recommendationText: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
    lineHeight: 20,
  },
  horizontalScroll: {
    marginHorizontal: -20,
    paddingHorizontal: 20,
  },
});

export default LearningDashboard;